import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Badge, Spinner } from 'react-bootstrap';
import { Bell, Check, CheckCheck, Trash2, X } from 'lucide-react';
import axiosInstance from '../../api/axiosConfig';

const TYPE_ICON = {
    leave_submitted:    '📋',
    leave_approved:     '✅',
    leave_rejected:     '❌',
    leave_needs_info:   '💬',
    leave_updated:      '💬',
    timesheet_submitted:'⏱',
    timesheet_approved: '✅',
    timesheet_rejected: '❌',
};

const timeAgo = (ts) => {
    const diff = (Date.now() - new Date(ts)) / 1000;
    if (diff < 60)    return 'just now';
    if (diff < 3600)  return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return new Date(ts).toLocaleDateString('en-IN', { day:'numeric', month:'short' });
};

const NotificationBell = () => {
    const [open,          setOpen]          = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unread,        setUnread]        = useState(0);
    const [loading,       setLoading]       = useState(false);
    const dropRef = useRef(null);

    const fetchNotifications = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/notifications');
            if (res.data.success) {
                setNotifications(res.data.data?.notifications || []);
                setUnread(res.data.data?.unread || 0);
            }
        } catch { /* silent */ }
    }, []);

    // Poll every 30 seconds for new notifications
    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClick = (e) => {
            if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const handleOpen = () => {
        setOpen(v => !v);
    };

    const markRead = async (id) => {
        try {
            await axiosInstance.patch(`/notifications/${id}/read`);
            setNotifications(p => p.map(n => n.id === id ? { ...n, is_read: 1 } : n));
            setUnread(p => Math.max(0, p - 1));
        } catch { /* silent */ }
    };

    const markAllRead = async () => {
        setLoading(true);
        try {
            await axiosInstance.patch('/notifications/read-all');
            setNotifications(p => p.map(n => ({ ...n, is_read: 1 })));
            setUnread(0);
        } catch { /* silent */ }
        finally { setLoading(false); }
    };

    const deleteNotif = async (id, e) => {
        e.stopPropagation();
        try {
            await axiosInstance.delete(`/notifications/${id}`);
            setNotifications(p => p.filter(n => n.id !== id));
            setUnread(p => Math.max(0, p - 1));
        } catch { /* silent */ }
    };

    return (
        <div ref={dropRef} style={{ position: 'relative' }}>
            {/* Bell button */}
            <button
                onClick={handleOpen}
                style={{
                    position: 'relative', background: 'none', border: 'none',
                    padding: '6px 8px', cursor: 'pointer', color: '#64748b',
                    borderRadius: 8,
                    background: open ? '#f1f5f9' : 'none',
                }}
            >
                <Bell size={20} />
                {unread > 0 && (
                    <span style={{
                        position: 'absolute', top: 2, right: 2,
                        background: '#ef4444', color: '#fff',
                        borderRadius: '50%', fontSize: 9, fontWeight: 700,
                        width: 16, height: 16,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '2px solid #fff',
                    }}>
                        {unread > 9 ? '9+' : unread}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {open && (
                <div style={{
                    position: 'absolute', top: '110%', right: 0,
                    width: 360, maxHeight: 480,
                    background: '#fff', borderRadius: 12,
                    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                    border: '1px solid #e2e8f0',
                    zIndex: 9999, overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                }}>
                    {/* Header */}
                    <div style={{
                        padding: '12px 16px', borderBottom: '1px solid #f1f5f9',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        background: '#f8fafc',
                    }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>
                            Notifications {unread > 0 && <Badge bg="danger" style={{ fontSize: 10 }}>{unread}</Badge>}
                        </span>
                        <div className="d-flex gap-2">
                            {unread > 0 && (
                                <button onClick={markAllRead} title="Mark all read"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4f46e5', padding: '2px 6px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                                    {loading ? <Spinner size="sm"/> : <><CheckCheck size={14} className="me-1"/>All read</>}
                                </button>
                            )}
                            <button onClick={() => setOpen(false)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2 }}>
                                <X size={16}/>
                            </button>
                        </div>
                    </div>

                    {/* List */}
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                        {notifications.length === 0 ? (
                            <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
                                <Bell size={32} style={{ marginBottom: 8, opacity: 0.3, display: 'block', margin: '0 auto 8px' }}/>
                                <div style={{ fontSize: 13 }}>No notifications yet</div>
                            </div>
                        ) : notifications.map(n => (
                            <div
                                key={n.id}
                                onClick={() => !n.is_read && markRead(n.id)}
                                style={{
                                    padding: '12px 16px',
                                    borderBottom: '1px solid #f8fafc',
                                    background: n.is_read ? '#fff' : '#f0f4ff',
                                    cursor: n.is_read ? 'default' : 'pointer',
                                    display: 'flex', gap: 10, alignItems: 'flex-start',
                                    transition: 'background 0.15s',
                                }}
                            >
                                {/* Icon */}
                                <div style={{ fontSize: 20, flexShrink: 0, lineHeight: 1.3 }}>
                                    {TYPE_ICON[n.type] || '🔔'}
                                </div>
                                {/* Content */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: n.is_read ? 400 : 600, color: '#1e293b', marginBottom: 2 }}>
                                        {n.title}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>
                                        {n.message}
                                    </div>
                                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                                        {timeAgo(n.created_at)}
                                    </div>
                                </div>
                                {/* Actions */}
                                <div style={{ flexShrink: 0, display: 'flex', gap: 4 }}>
                                    {!n.is_read && (
                                        <button onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                                            title="Mark as read"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4f46e5', padding: 3, borderRadius: 4 }}>
                                            <Check size={14}/>
                                        </button>
                                    )}
                                    <button onClick={(e) => deleteNotif(n.id, e)}
                                        title="Delete"
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', padding: 3, borderRadius: 4 }}>
                                        <Trash2 size={14}/>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
